import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Fab } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { CustomTemplateChip } from "./custom-template-chip";
import { ImageLoading } from "./image-loading";

import type { Loadable } from "../../utils/jotai-loadable";
import type { Dimensions } from "../design-huddle/types";
import type { MultiformatDimensions } from "../multiformat/store/types";
import type { FC, RefObject } from "react";

export type PreviewEditHandler = () => void;

const useContainerDimensions = (
  myRef: RefObject<HTMLDivElement | null>,
  ratio: number,
  hidden: boolean,
): { width: number; height: number } => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const getDimensions = (): Dimensions => ({
      width: myRef.current?.offsetWidth || 0,
      height: myRef.current?.offsetHeight || 0,
    });

    const handleResize = (): void => {
      setDimensions(getDimensions());
    };

    if (myRef.current) {
      setDimensions(getDimensions());
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- ratio and hidden change the container's layout, so re-measure when they change
  }, [myRef, ratio, hidden]);

  return dimensions;
};

export const ContentPreview: FC<{
  image: Loadable<Promise<{ url: string; customTemplate: boolean } | null>>;
  hidden: boolean;
  dimensions: MultiformatDimensions;
  designer?: string | null;
  onEdit?: PreviewEditHandler;
  onApplyToAll?: PreviewEditHandler;
  imageUrl?: string;
  contentTypeName?: string;
  onLoaded?: () => void;
}> = ({
  image,
  hidden,
  dimensions,
  designer,
  onEdit,
  onApplyToAll,
  imageUrl,
  contentTypeName,
  onLoaded,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const ratio = dimensions.width && dimensions.height ? dimensions.height / dimensions.width : 0;

  const containerDimensions = useContainerDimensions(ref, ratio, hidden);

  useEffect(() => {
    if (image?.state === "hasData" && imageUrl) {
      onLoaded?.();
    }
  }, [image, imageUrl, onLoaded]);

  if (ratio === 0) return null;

  return (
    <Box
      ref={ref}
      data-testid="create-content-preview-image"
      data-state={image.state}
      sx={[
        (theme) => ({
          ...(hidden ? { display: "none" } : {}),
          borderStyle: "solid",
          borderWidth: 2,
          borderColor: "transparent",
          position: "relative",
          "&:hover": {
            borderColor: theme.palette.primary.main,
            "& .action-buttons": {
              opacity: 1,
            },
          },
          backgroundImage: `url(${image.state === "hasData" && imageUrl ? imageUrl : ""})`,
          boxSizing: "content-box",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundOrigin: "content-box",
          height: `${containerDimensions.width * ratio}px`,
        }),
      ]}
    >
      {image?.state === "hasData" && image.data?.customTemplate && imageUrl && (
        <CustomTemplateChip designer={designer} />
      )}
      {image.state === "hasData" && imageUrl && (onEdit || onApplyToAll) && (
        <Box
          className="action-buttons"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            opacity: 0,
            transition: "opacity 0.2s ease-in-out",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {onEdit && (
            <Fab
              variant="extended"
              size="large"
              onClick={onEdit}
              data-analytics-id="create-content-launch-project"
              color="inherit"
              sx={(theme) => ({
                textTransform: "none",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              })}
            >
              <EditIcon sx={{ mr: 1 }} />
              Customize this template
            </Fab>
          )}
          {onApplyToAll && (
            <Fab
              variant="extended"
              size="large"
              onClick={onApplyToAll}
              data-analytics-id="create-content-apply-to-all"
              sx={{
                backgroundColor: "background.paper",
                textTransform: "none",
                fontWeight: 400,
                color: "text.secondary",
              }}
            >
              <CheckCircleOutlined sx={{ mr: 1, color: "primary.main" }} />
              {contentTypeName?.toLowerCase() === "video"
                ? "Use this video on all templates"
                : "Use this image on all templates"}
            </Fab>
          )}
        </Box>
      )}
      {(image.state === "loading" || !imageUrl) && <ImageLoading />}
    </Box>
  );
};
